import React, { useState, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from "./Sidebar";
import Topbar from "./Topbar";
import SearchOverlay from "./SearchOverlay";
import { useCookieConsent } from "../../context/CookieConsentContext";

const DashboardLayout = ({
	children,
	user,
	activeSection,
	onSectionChange,
	onLogin,
	onSignUp,
	onLogout,
	onCreateNew,
	onSearch,
	prebuiltGuides = [],
	myGuides = [],
	imageMap = {},
	travelCard = null,
	countriesCount = 0,
	/** Solo en DashboardPage (header glass sobre hero en móvil) */
	isDashboardHome = false,
	/** Guía "próximamente disponible": al hacer clic en búsqueda, abrir modal en lugar de ir al preview */
	onComingSoonGuide = null,
	quietCreate = false,
}) => {
	// 920px y por debajo = móvil (header glass sobre hero, sin barra blanca arriba)
	const isMobile = useMediaQuery("(max-width:920px)");
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);
	const { openPreferences } = useCookieConsent();

	// Close mobile menu on resize to desktop
	useEffect(() => {
		if (!isMobile) {
			setMobileMenuOpen(false);
		}
	}, [isMobile]);

	const currentSidebarWidth = isMobile
		? 0
		: sidebarCollapsed
			? SIDEBAR_COLLAPSED_WIDTH
			: SIDEBAR_WIDTH;

	return (
		<Box
			sx={{
				minHeight: "100vh",
				maxWidth: "100vw",
				overflowX: "hidden",
				background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
				display: "flex",
				position: "relative",
			}}
		>
			{/* Background Pattern — sin tonos azul/morado para evitar sombra azul */}
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					backgroundImage: "none",
					pointerEvents: "none",
					zIndex: 0,
				}}
			/>

			{/* Sidebar */}
			<Sidebar
				user={user}
				activeSection={activeSection}
				onSectionChange={onSectionChange}
				collapsed={sidebarCollapsed}
				onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
				isMobile={isMobile}
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
				travelCard={travelCard}
				countriesCount={countriesCount}
				quietCreate={quietCreate}
			/>

			{/* Main Content Area */}
			<Box
				component="main"
				sx={{
					flex: 1,
					minWidth: 0,
					marginLeft: `${currentSidebarWidth}px`,
					transition: "margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
					minHeight: "100vh",
					display: "flex",
					flexDirection: "column",
					position: "relative",
					zIndex: 1,
					overflowX: "hidden",
				}}
			>
				{/* Topbar */}
				<Topbar
					user={user}
					onLogin={onLogin}
					onSignUp={onSignUp}
					onLogout={onLogout}
					onCreateNew={onCreateNew}
					onMenuToggle={() => setMobileMenuOpen(true)}
					isMobile={isMobile}
					isDashboardHome={isDashboardHome}
					onSearch={onSearch}
					onSearchOpen={() => setSearchOpen(true)}
				/>

				{/* Search Overlay */}
				<SearchOverlay
					open={searchOpen}
					onClose={() => setSearchOpen(false)}
					prebuiltGuides={prebuiltGuides}
					myGuides={myGuides}
					imageMap={imageMap}
					onComingSoonGuide={onComingSoonGuide}
				/>

				{/* Page Content */}
				<Box
					sx={{
						flex: 1,
						p: { xs: 2, sm: 3, md: 4 },
						px: { xs: isDashboardHome ? 0 : 2, sm: 3, md: 4 },
						pt: { xs: 0, md: 3 },
						maxWidth: 1440,
						width: "100%",
						mx: "auto",
						overflowX: "hidden",
					}}
				>
					{children}
				</Box>

				{/* Footer */}
				<Box
					component="footer"
					sx={{
						py: 3,
						px: { xs: 2, md: 4 },
						borderTop: "1px solid rgba(0, 0, 0, 0.04)",
						backgroundColor: "rgba(255, 255, 255, 0.5)",
						backdropFilter: "blur(10px)",
					}}
				>
					<Box
						sx={{
							maxWidth: 1440,
							mx: "auto",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 2,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.75,
								color: "#8B8FA3",
								fontSize: "0.813rem",
							}}
						>
							<span>© {new Date().getFullYear()} Viajes Molones</span>
							<span>·</span>
							<span>Hecho con 💜</span>
						</Box>
						<Box
							sx={{
								display: "flex",
								gap: 3,
							}}
						>
							<Box
								component="button"
								type="button"
								onClick={openPreferences}
								sx={{
									background: "none",
									border: "none",
									padding: 0,
									cursor: "pointer",
									color: "#8B8FA3",
									fontSize: "0.813rem",
									fontFamily: "inherit",
									transition: "color 0.15s ease",
									"&:hover": {
										color: "#6366F1",
									},
								}}
							>
								Configurar cookies
							</Box>
							{[
								{ label: "Aviso legal", to: "/legal/aviso-legal" },
								{ label: "Privacidad", to: "/legal/privacidad" },
								{ label: "Términos", to: "/legal/terminos" },
								{ label: "Cookies", to: "/legal/cookies" },
							].map(({ label, to }) => (
								<Box
									key={to}
									component={Link}
									to={to}
									target="_blank"
									rel="nofollow noopener noreferrer"
									sx={{
										color: "#8B8FA3",
										fontSize: "0.813rem",
										textDecoration: "none",
										transition: "color 0.15s ease",
										"&:hover": {
											color: "#6366F1",
										},
									}}
								>
									{label}
								</Box>
							))}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default DashboardLayout;
